import { Controller } from "@hotwired/stimulus"

// Home page only. A single navbar slot next to the logo is shared between the
// desktop menu and the hero search:
//   • default (top) → the desktop menu sits in the slot, search lives in the hero
//   • scrolled      → search docks into the slot, the menu drops to its own row
// The menu is server-rendered straight into the slot, so the default state needs
// no DOM moves (no flash). Each element is a single node that is moved (never
// duplicated), so IDs and their listeners stay intact. Both the navbar bottom and
// the sentinel are measured live so a growing navbar can't make the swap oscillate.
export default class extends Controller {
  static targets = ["search", "origin", "sentinel"]
  static values = { dockId: String }

  // Dead-zone between the dock/undock thresholds to avoid oscillation.
  HYSTERESIS = 24
  // Crossfade for the docking swap: fade out, reparent while invisible, fade in.
  DURATION = 180
  EASING = "ease"

  connect() {
    this.slot = document.getElementById(this.dockIdValue)
    if (!this.slot) return

    this.desktop = window.matchMedia("(min-width: 768px)")
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.navbar = document.getElementById("main-navbar")
    this.searchEl = this.searchTarget
    this.menuEl = document.getElementById("desktop-menu")
    this.menuRow = document.getElementById("navbar-menu-row")
    // The bordered Row 2 wrapper — hidden while the menu lives up by the logo.
    this.menuRowWrapper = this.menuRow?.parentElement
    this.state = null

    this.onScroll = this.onScroll.bind(this)
    this.boundReset = this.reset.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    document.addEventListener("turbo:before-cache", this.boundReset)
    this.evaluate()
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    document.removeEventListener("turbo:before-cache", this.boundReset)
    this.reset()
  }

  onScroll() {
    if (this.ticking) return
    this.ticking = true
    requestAnimationFrame(() => {
      this.evaluate()
      this.ticking = false
    })
  }

  evaluate() {
    if (!this.desktop.matches) return this.toDefault()

    const navBottom = this.navbar.getBoundingClientRect().bottom
    const sentinelTop = this.sentinelTarget.getBoundingClientRect().top
    const threshold = this.state === "scrolled" ? navBottom + this.HYSTERESIS : navBottom

    if (sentinelTop < threshold) this.toScrolled()
    else this.toDefault()
  }

  // Default: the menu in the slot by the logo, search in the hero. Matches the
  // server-rendered DOM, so the moves are no-ops on first paint.
  toDefault() {
    if (this.state === "default") return
    this.state = "default"
    this.#crossfade(() => {
      this.originTarget.style.height = ""
      this.originTarget.appendChild(this.searchEl)
      if (this.menuEl) this.slot.appendChild(this.menuEl)
      this.menuRowWrapper?.classList.add("hidden")
      this.#setMenuAlignment("center")
    })
  }

  // Scrolled: search docked in the slot, the menu dropped into its own row.
  toScrolled() {
    if (this.state === "scrolled") return
    this.state = "scrolled"
    this.#crossfade(() => {
      this.menuRowWrapper?.classList.remove("hidden")
      if (this.menuEl && this.menuRow) this.menuRow.appendChild(this.menuEl)
      // Reserve the vacated hero space so the stationary sentinel doesn't shift.
      this.originTarget.style.height = `${this.searchEl.offsetHeight}px`
      this.slot.appendChild(this.searchEl)
      this.#setMenuAlignment("start")
    })
  }

  // Crossfade the swap instead of moving the nodes across the screen: fade both
  // out in place, reparent them while invisible, then fade them back in. No
  // motion — only opacity — so nothing flies from corner to corner.
  #crossfade(mutate) {
    const els = this.#movableEls

    if (this.reducedMotion.matches) return mutate()

    els.forEach((el) => {
      el.style.transition = `opacity ${this.DURATION}ms ${this.EASING}`
      el.style.opacity = "0"
    })

    setTimeout(() => {
      mutate()
      requestAnimationFrame(() => {
        els.forEach((el) => {
          el.style.opacity = ""
          el.addEventListener("transitionend", this.#clearFade, { once: true })
        })
      })
    }, this.DURATION)
  }

  #clearFade(event) {
    event.currentTarget.style.transition = ""
    event.currentTarget.style.opacity = ""
  }

  // Restore the server-rendered DOM (menu in the slot, search in the hero) so a
  // cached Turbo snapshot restores in the default state without a flash.
  reset() {
    this.#movableEls.forEach((el) => {
      el.style.transition = ""
      el.style.opacity = ""
    })
    this.originTarget.style.height = ""
    this.originTarget.appendChild(this.searchEl)
    if (this.menuEl) this.slot.appendChild(this.menuEl)
    this.menuRowWrapper?.classList.add("hidden")
    this.#setMenuAlignment("center")
    this.state = null
  }

  // The two nodes that get reparented on the docking swap.
  get #movableEls() {
    return [this.searchEl, this.menuEl].filter(Boolean)
  }

  #setMenuAlignment(align) {
    const ul = this.menuEl?.querySelector("ul")
    if (!ul) return
    const centered = align === "center"
    ul.classList.toggle("justify-center", centered)
    ul.classList.toggle("justify-start", !centered)
  }
}
